import React, { useState } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Label } from './Label';
import { Palette, Check } from 'lucide-react';

interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const predefinedColors = [
  '#3B82F6', // Blue
  '#1E40AF', // Dark Blue
  '#F59E0B', // Amber
  '#10B981', // Emerald
  '#EF4444', // Red
  '#8B5CF6', // Violet
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6B7280', // Gray
  '#000000', // Black
];

const ColorPicker: React.FC<ColorPickerProps> = ({ 
  label, 
  value, 
  onChange, 
  className = '' 
}) => {
  const [showPalette, setShowPalette] = useState(false);

  const handleColorSelect = (color: string) => {
    onChange(color);
    setShowPalette(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    if (color.match(/^#[0-9A-Fa-f]{6}$/)) {
      onChange(color);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label className="capitalize text-sm font-medium">
        {label === 'primary' ? 'Primária' :
         label === 'secondary' ? 'Secundária' :
         label === 'accent' ? 'Destaque' :
         label === 'background' ? 'Fundo' :
         label === 'text' ? 'Texto' : label}
      </Label>
      
      <div className="flex items-center gap-2">
        {/* Color Input */}
        <Input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-10 p-1 border rounded cursor-pointer"
          title="Selecionar cor"
        />
        
        {/* Text Input */}
        <Input
          value={value}
          onChange={handleInputChange}
          placeholder="#000000"
          className="flex-1 font-mono text-sm"
          title="Digite o código da cor"
        />
        
        {/* Palette Button */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowPalette(!showPalette)}
          className="px-3"
        >
          <Palette className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Predefined Colors Palette */}
      {showPalette && (
        <div className="mt-2 p-3 border rounded-lg bg-white shadow-lg">
          <div className="grid grid-cols-6 gap-2">
            {predefinedColors.map((color) => (
              <button
                key={color}
                type="button"
                className={`w-8 h-8 rounded border-2 transition-all hover:scale-110 ${
                  value === color ? 'border-gray-900 ring-2 ring-gray-300' : 'border-gray-200'
                }`}
                style={{ backgroundColor: color }}
                onClick={() => handleColorSelect(color)}
                title={color}
              >
                {value === color && (
                  <Check className="h-4 w-4 text-white m-auto" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
